<script setup lang="ts">
useSeoMeta({
  title: 'Excel Register Migration and Validation Pack | QikSolve',
  description:
    'Move a suitable Excel quality register to SharePoint with agreed migration and a customer-reviewable validation pack prepared for authorised sign-off.',
  ogTitle: 'Excel Register Migration and Validation Pack',
  ogDescription:
    'A practical register migration pathway with a customer-reviewable validation pack prepared for authorised sign-off.',
  ogType: 'website',
  ogImage: 'https://www.qiksolve.com/images/qiksolve_cropped-logo.png',
  ogUrl: 'https://www.qiksolve.com/solutions/excel-register-to-sharepoint/',
  twitterCard: 'summary_large_image',
});
</script>

<template>
  <div>
    <section class="bg-brand-950 text-white">
      <div class="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-center lg:px-8 lg:py-24">
        <div>
          <p class="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-blue-200">
            Excel quality register to SharePoint
          </p>
          <h1 class="max-w-3xl text-4xl font-bold leading-tight sm:text-5xl">
            Excel quality register to SharePoint migration
          </h1>
          <p class="mt-6 max-w-2xl text-lg leading-8 text-slate-200">
            Move towards a shared register with clearer ownership and visibility. We start with the
            register your team uses, understand what it needs to do, then agree the configuration,
            migration and evidence package before implementation.
          </p>
          <NuxtLink
            to="/contact/"
            class="mt-8 inline-flex min-h-12 items-center justify-center rounded-md bg-white px-6 py-3 font-semibold text-brand-950 underline-offset-4 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
          >
            Book a discovery call
          </NuxtLink>
        </div>

        <aside class="rounded-xl border border-white/20 bg-white p-6 text-slate-900 shadow-card">
          <p class="text-sm font-semibold uppercase tracking-wide text-brand-800">
            Migration and validation sign-off pack
          </p>
          <p class="mt-3 text-3xl font-bold text-brand-950">
            AUD 9,950 <span class="text-base font-medium">+ GST</span>
          </p>
          <p class="mt-3 text-sm leading-6 text-slate-700">
            For a suitable, straightforward register scope. We confirm fit and inclusions with you
            before you commit.
          </p>
          <p class="mt-4 border-t border-slate-200 pt-4 text-sm leading-6 text-slate-700">
            Includes the agreed configuration and migration, reconciliation, and a customer-reviewable
            validation evidence pack prepared for your authorised sign-off.
          </p>
        </aside>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div class="max-w-3xl">
        <p class="text-sm font-semibold uppercase tracking-wide text-brand-800">
          Make the register work for the team
        </p>
        <h2 class="mt-3 text-3xl font-bold text-slate-950">
          Improve the everyday work around your quality register
        </h2>
        <p class="mt-5 text-lg leading-8 text-slate-700">
          When updates are inconsistent or responsibility is hard to see, it can be difficult to
          understand what needs attention. A register configured around agreed fields, owners,
          statuses and views can give the team a clearer shared way to work.
        </p>
      </div>

      <div class="mt-10 grid gap-5 md:grid-cols-3">
        <article class="rounded-lg border border-slate-200 bg-white p-6 shadow-card">
          <h3 class="text-lg font-bold text-slate-950">
            Consistent information
          </h3>
          <p class="mt-3 leading-7 text-slate-700">
            Agree the fields and controlled values that fit the register's intended use.
          </p>
        </article>
        <article class="rounded-lg border border-slate-200 bg-white p-6 shadow-card">
          <h3 class="text-lg font-bold text-slate-950">
            Visible responsibility
          </h3>
          <p class="mt-3 leading-7 text-slate-700">
            Set up owner and status information so people can see how work is progressing.
          </p>
        </article>
        <article class="rounded-lg border border-slate-200 bg-white p-6 shadow-card">
          <h3 class="text-lg font-bold text-slate-950">
            Useful working views
          </h3>
          <p class="mt-3 leading-7 text-slate-700">
            Agree views that help the team review the information relevant to its work.
          </p>
        </article>
      </div>

      <p class="mt-8 max-w-3xl text-sm leading-6 text-slate-600">
        The fields, views, access and change-history arrangements depend on the agreed design and
        your Microsoft 365 configuration. They are confirmed during scoping, not assumed.
      </p>
    </section>

    <section class="bg-slate-50">
      <div class="mx-auto grid max-w-6xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-20">
        <div>
          <p class="text-sm font-semibold uppercase tracking-wide text-brand-800">
            A practical transition
          </p>
          <h2 class="mt-3 text-3xl font-bold text-slate-950">
            From your current register to an agreed way of working
          </h2>
          <p class="mt-5 leading-7 text-slate-700">
            The work begins with fit and intended use. We agree what will move, how the destination
            will be configured, which checks and evidence are in scope, and what your team needs to
            review and accept.
          </p>
        </div>
        <ol class="space-y-6">
          <li class="flex gap-4">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 font-bold text-brand-900">1</span>
            <div>
              <h3 class="font-bold text-slate-950">
                Understand the register
              </h3>
              <p class="mt-1 leading-7 text-slate-700">
                Discuss intended use, users, information and the decisions it supports.
              </p>
            </div>
          </li>
          <li class="flex gap-4">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 font-bold text-brand-900">2</span>
            <div>
              <h3 class="font-bold text-slate-950">
                Agree the scope
              </h3>
              <p class="mt-1 leading-7 text-slate-700">
                Confirm fit, mapping, configuration, responsibilities and the evidence package.
              </p>
            </div>
          </li>
          <li class="flex gap-4">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 font-bold text-brand-900">3</span>
            <div>
              <h3 class="font-bold text-slate-950">
                Configure and migrate
              </h3>
              <p class="mt-1 leading-7 text-slate-700">
                Set up the agreed SharePoint list, transfer the scoped data and reconcile the result.
              </p>
            </div>
          </li>
          <li class="flex gap-4">
            <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 font-bold text-brand-900">4</span>
            <div>
              <h3 class="font-bold text-slate-950">
                Review and hand over
              </h3>
              <p class="mt-1 leading-7 text-slate-700">
                Complete agreed checks, review discrepancies and hand over the configuration and evidence.
              </p>
            </div>
          </li>
        </ol>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div class="grid gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <p class="text-sm font-semibold uppercase tracking-wide text-brand-800">
            Validation sign-off pack
          </p>
          <h2 class="mt-3 text-3xl font-bold text-slate-950">
            A reviewable pack for your validation sign-off
          </h2>
          <p class="mt-5 leading-7 text-slate-700">
            The agreed output is a customer-reviewable validation evidence pack, prepared for your
            authorised Quality or validation reviewer to assess and sign off. For the agreed scope,
            the pack includes:
          </p>
          <ul class="mt-4 list-disc space-y-2 pl-5 leading-7 text-slate-700">
            <li>intended use, requirements and risk rationale;</li>
            <li>configuration, data-mapping and migration records;</li>
            <li>traceability and agreed verification protocols with results;</li>
            <li>discrepancy records, disposition and migration reconciliation; and</li>
            <li>a validation summary with a section for customer review and sign-off.</li>
          </ul>
          <p class="mt-4 leading-7 text-slate-700">
            The pack supports your validation review; it does not itself constitute your approval.
            Your organisation retains responsibility for reviewing the evidence, deciding whether
            it is sufficient, signing off, and maintaining ongoing control.
          </p>
        </div>
        <aside class="rounded-xl border border-slate-200 bg-brand-50 p-6 sm:p-8">
          <h2 class="text-xl font-bold text-slate-950">
            Is this package a fit?
          </h2>
          <p class="mt-3 leading-7 text-slate-700">
            The standard scope is designed for one suitable, flat register of current data moving
            to one customer SharePoint list. Workbooks with more complex formulas, linked records,
            history, workflows, integrations or data-remediation needs may need further assessment
            and a separate scope.
          </p>
          <p class="mt-4 leading-7 text-slate-700">
            A fit discussion can also conclude that retaining or remediating the Excel register is
            the better next step.
          </p>
          <p class="mt-4 leading-7 text-slate-700">
            If an audit has raised concerns about controls or evidence, start by assessing the
            finding and intended use. Migration is not an automatic recommendation.
          </p>
          <NuxtLink
            to="/explore-by-need/validate-excel-quality-register/"
            class="mt-5 inline-flex font-semibold text-brand-800 underline underline-offset-4 hover:text-brand-700"
          >
            Start with the register assessment questions
          </NuxtLink>
        </aside>
      </div>
    </section>

    <section class="bg-slate-50">
      <div class="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <div class="max-w-3xl">
          <p class="text-sm font-semibold uppercase tracking-wide text-brand-800">
            Scope and responsibilities
          </p>
          <h2 class="mt-3 text-3xl font-bold text-slate-950">
            Agree what is included before implementation
          </h2>
          <p class="mt-5 leading-7 text-slate-700">
            The standard price applies only when the register is suitable for the agreed package.
            Additional registers, complex transformations, broader workflow design and other work
            outside the confirmed scope are assessed separately before you decide whether to proceed.
          </p>
        </div>
        <div class="mt-8 grid gap-6 md:grid-cols-2">
          <div class="rounded-lg border border-slate-200 bg-white p-6">
            <h3 class="font-bold text-slate-950">
              Your team provides
            </h3>
            <ul class="mt-4 list-disc space-y-2 pl-5 leading-7 text-slate-700">
              <li>a named process or Quality owner and a prepared source register;</li>
              <li>IT decisions and permissions needed for the agreed configuration; and</li>
              <li>review, acceptance and approval by the appropriate people in your organisation.</li>
            </ul>
          </div>
          <div class="rounded-lg border border-slate-200 bg-white p-6">
            <h3 class="font-bold text-slate-950">
              Separate from the package
            </h3>
            <ul class="mt-4 list-disc space-y-2 pl-5 leading-7 text-slate-700">
              <li>Microsoft licensing, which remains subject to your organisation's needs;</li>
              <li>independent PharmOut review, if requested, which is separately quoted and subject to availability; and</li>
              <li>any work that falls outside the agreed register, migration or evidence scope.</li>
            </ul>
          </div>
        </div>
        <p class="mt-6 max-w-4xl text-sm leading-6 text-slate-600">
          This is a QikSolve implementation service; an INQ subscription is not required. The
          service supports your validation work and does not itself constitute regulatory approval
          or a guarantee of compliance or audit outcomes.
        </p>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div class="grid gap-10 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div>
          <p class="text-sm font-semibold uppercase tracking-wide text-brand-800">
            Commercial steps
          </p>
          <h2 class="mt-3 text-3xl font-bold text-slate-950">
            Start with fit, then agree the work
          </h2>
          <ol class="mt-6 list-inside list-decimal space-y-3 leading-7 text-slate-700">
            <li>Book a free discovery call of up to 30 minutes about one register.</li>
            <li>If needed, commission an optional AUD 950 + GST assessment.</li>
            <li>Confirm scope and quote before the standard AUD 9,950 + GST package begins.</li>
            <li>Review the implementation and evidence, then accept the handover.</li>
          </ol>
          <p class="mt-5 leading-7 text-slate-700">
            If assessment outputs are reused for the migration, the AUD 950 + GST assessment is
            credited against the standard package. The remaining balance is AUD 9,000 + GST.
          </p>
          <NuxtLink
            to="/contact/"
            class="mt-8 inline-flex min-h-12 items-center justify-center rounded-md bg-brand-700 px-6 py-3 font-semibold text-white underline-offset-4 hover:bg-brand-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-700"
          >
            Book a discovery call
          </NuxtLink>
        </div>

        <aside class="h-fit rounded-xl border border-slate-200 bg-white p-6 shadow-card">
          <h2 class="text-xl font-bold text-slate-950">
            Questions before you decide?
          </h2>
          <dl class="mt-5 space-y-5">
            <div>
              <dt class="font-semibold text-slate-950">
                Does every register need to move?
              </dt>
              <dd class="mt-1 leading-7 text-slate-700">
                No. Retaining or remediating Excel can be the right outcome.
              </dd>
            </div>
            <div>
              <dt class="font-semibold text-slate-950">
                Does the package require INQ?
              </dt>
              <dd class="mt-1 leading-7 text-slate-700">
                No. Microsoft licensing is separate.
              </dd>
            </div>
            <div>
              <dt class="font-semibold text-slate-950">
                What does the CSV evidence work cover?
              </dt>
              <dd class="mt-1 leading-7 text-slate-700">
                Computerised system validation activities are scoped to the agreed register and
                identify who prepares, executes, reviews and approves each activity.
              </dd>
            </div>
            <div>
              <dt class="font-semibold text-slate-950">
                Will all history and linked records move?
              </dt>
              <dd class="mt-1 leading-7 text-slate-700">
                Not by assumption. We assess these needs and agree what is in scope before migration.
              </dd>
            </div>
            <div>
              <dt class="font-semibold text-slate-950">
                Who approves the validation evidence?
              </dt>
              <dd class="mt-1 leading-7 text-slate-700">
                Your organisation retains its review, acceptance and approval decisions.
              </dd>
            </div>
          </dl>
        </aside>
      </div>
    </section>

    <section class="bg-brand-950 text-white">
      <div class="mx-auto max-w-6xl px-4 py-14 text-center sm:px-6 lg:px-8">
        <h2 class="text-3xl font-bold">
          Talk through one register and its intended use
        </h2>
        <p class="mx-auto mt-4 max-w-2xl leading-7 text-slate-200">
          A short fit discussion is the first step. If migration is not the right answer, the
          assessment can point to a retain or remediate path instead.
        </p>
        <NuxtLink
          to="/contact/"
          class="mt-7 inline-flex min-h-12 items-center justify-center rounded-md bg-white px-6 py-3 font-semibold text-brand-950 underline-offset-4 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          Book a discovery call
        </NuxtLink>
      </div>
    </section>
  </div>
</template>
